import { useEffect, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';

import ConnectionContainer from '../../../containers/Onboarding/Connection/ConnectionContainer';
import {
  inverterManufacturers,
  manufacturerToSlug,
} from '../../../containers/Onboarding/Connection/inverterManufacturers';

import ConnectedStep from './ConnectedStep';
import SelectManufacturerStep from './SelectManufacturerStep';

type Step = 'select-manufacturer' | 'connect' | 'connected';

interface InverterConnectionModalProps {
  show: boolean;
  onClose: () => void;
  onConnected: () => void;
  /** Manufacturer display name from onboarding data, e.g. "AlphaEss". When recognised, skips the picker step. */
  initialManufacturer?: string;
}

const InverterConnectionModal = ({ show, onClose, onConnected, initialManufacturer }: InverterConnectionModalProps) => {
  const [step, setStep] = useState<Step>('select-manufacturer');
  const [manufacturerSlug, setManufacturerSlug] = useState<string | null>(null);
  const [skippedPicker, setSkippedPicker] = useState(false);

  // Track the latest value without re-triggering the effect below, so a slower
  // `getAdditionalInformation` response arriving after the modal is already open
  // (and the user may have already picked a manufacturer) can't clobber their progress.
  const initialManufacturerRef = useRef(initialManufacturer);
  useEffect(() => {
    initialManufacturerRef.current = initialManufacturer;
  }, [initialManufacturer]);

  useEffect(() => {
    if (!show) return;

    const currentInitialManufacturer = initialManufacturerRef.current;
    const slug = currentInitialManufacturer ? manufacturerToSlug(currentInitialManufacturer) : undefined;
    if (slug && inverterManufacturers[slug]) {
      setManufacturerSlug(slug);
      setStep('connect');
      setSkippedPicker(true);
    } else {
      setManufacturerSlug(null);
      setStep('select-manufacturer');
      setSkippedPicker(false);
    }
    // Only re-initialise when the modal opens, not on every initialManufacturer change.
  }, [show]);

  return (
    <Modal aria-labelledby="inverter-connection-heading" centered show={show} onHide={onClose}>
      <Modal.Body className="p-4">
        {step === 'select-manufacturer' && (
          <SelectManufacturerStep
            onSubmit={(slug) => {
              setManufacturerSlug(slug);
              setStep('connect');
            }}
            onCancel={onClose}
          />
        )}
        {step === 'connect' && manufacturerSlug && (
          <ConnectionContainer
            manufacturer={manufacturerSlug}
            mode="edit"
            onSuccess={() => setStep('connected')}
            onCancel={onClose}
            onBack={
              skippedPicker
                ? () => {
                    setSkippedPicker(false);
                    setStep('select-manufacturer');
                  }
                : undefined
            }
          />
        )}
        {step === 'connected' && <ConnectedStep onContinue={onConnected} />}
      </Modal.Body>
    </Modal>
  );
};

export default InverterConnectionModal;
